import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Phone, Mail, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT } from "@/data/assets";
import { TurnstileWidget } from "./TurnstileWidget";
import { useLang } from "@/i18n/LanguageContext";
import { reveal } from "@/hooks/reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-[#0e0e0e] border-0 border-b border-white/15 rounded-none h-12 text-white placeholder:text-white/25 font-body focus-visible:ring-0 focus-visible:border-[var(--al-gold)] transition-colors duration-300 px-0";

export const Contact = ({ productRef, clearRef }) => {
  const { t, lang } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ full_name: "", corporate_email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const turnstileRef = useRef(null);

  const submit = (e) => {
    e.preventDefault();
    if (!form.full_name || !form.corporate_email || !form.subject || !form.message) {
      toast.error(c.errorFill);
      return;
    }
    setLoading(true);
    const started = turnstileRef.current?.execute();
    if (!started) {
      setLoading(false);
      toast.error(c.errorGeneric);
    }
  };

  const handleToken = async (token) => {
    try {
      const payload = { ...form, lang, turnstile_token: token };
      if (productRef) payload.product_ref = productRef;
      await axios.post(`${API}/inquiries`, payload);
      setDone(true);
      toast.success(c.successToast);
      setForm({ full_name: "", corporate_email: "", subject: "", message: "" });
      clearRef?.();
    } catch (err) {
      toast.error(err?.response?.status === 429 ? c.errorRateLimit : c.errorGeneric);
    } finally {
      setLoading(false);
      turnstileRef.current?.reset();
    }
  };

  const handleTsError = () => {
    turnstileRef.current?.reset();
    if (loading) {
      setLoading(false);
      toast.error(c.errorGeneric);
    }
  };

  return (
    <section id="contact" className="relative bg-black/40 py-24 md:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left */}
          <motion.div className="lg:col-span-5" {...reveal()}>
            <span className="font-mono-tech text-[11px] tracking-[0.35em] uppercase text-[var(--al-gold)]">
              {c.eyebrow}
            </span>
            <h2 className="font-cinzel font-bold text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
              {c.title[0]}<br />{c.title[1]}
            </h2>
            <p className="font-body text-white/55 text-sm md:text-base leading-relaxed mt-6 max-w-md">
              {c.intro}
            </p>

            <div className="mt-12 space-y-6">
              <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                <div className="w-11 h-11 border border-white/10 flex items-center justify-center group-hover:border-[var(--al-gold)]/50 transition-colors duration-300">
                  <Phone className="w-4 h-4 text-[var(--al-gold)]" />
                </div>
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">{c.hotlineLabel}</div>
                  <div className="font-body text-white text-sm md:text-base mt-0.5">{CONTACT.phone}</div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                <div className="w-11 h-11 border border-white/10 flex items-center justify-center group-hover:border-[var(--al-gold)]/50 transition-colors duration-300">
                  <Mail className="w-4 h-4 text-[var(--al-gold)]" />
                </div>
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">{c.deskLabel}</div>
                  <div className="font-body text-white text-sm md:text-base mt-0.5 break-all">{CONTACT.email}</div>
                </div>
              </a>
            </div>
          </motion.div>

          {/* Right — form */}
          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="border border-[var(--al-gold)]/40 bg-[#0e0e0e] p-10 md:p-14 flex flex-col items-start"
                data-testid="contact-success"
              >
                <CheckCircle2 className="w-10 h-10 text-[var(--al-gold)]" />
                <h3 className="font-cinzel font-bold text-white text-2xl md:text-3xl mt-6">
                  {c.successTitle}
                </h3>
                <p className="font-body text-white/60 text-sm md:text-base leading-relaxed mt-4 max-w-md">
                  {c.successBody}
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="btn-gold mt-8 border border-white/15 px-6 py-3 font-body text-[11px] tracking-[0.2em] uppercase text-white"
                  data-testid="contact-reset-btn"
                >
                  <span>{c.submitAnother}</span>
                </button>
              </motion.div>
            ) : (
              <motion.form {...reveal(0.1)} onSubmit={submit} className="border border-white/10 bg-[#0e0e0e] p-7 md:p-10" data-testid="contact-form">
                {productRef && (
                  <div className="mb-8 flex items-center justify-between gap-3 border border-[var(--al-gold)]/30 bg-[var(--al-gold)]/[0.06] px-4 py-3">
                    <span className="font-mono-tech text-[12px] text-[var(--al-gold)]">
                      {c.enquiringAbout} {productRef}
                    </span>
                    <button type="button" onClick={clearRef} className="text-white/40 hover:text-white text-xs" data-testid="clear-product-ref">
                      {c.clear}
                    </button>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">{c.labels.fullName}</Label>
                    <Input className={`${inputCls} mt-2`} value={form.full_name} onChange={set("full_name")} placeholder={c.ph.fullName} data-testid="input-full-name" />
                  </div>
                  <div>
                    <Label className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">{c.labels.email}</Label>
                    <Input type="email" className={`${inputCls} mt-2`} value={form.corporate_email} onChange={set("corporate_email")} placeholder={c.ph.email} data-testid="input-email" />
                  </div>
                </div>
                <div className="mt-6">
                  <Label className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">{c.labels.subject}</Label>
                  <Input className={`${inputCls} mt-2`} value={form.subject} onChange={set("subject")} placeholder={c.ph.subject} data-testid="input-subject" />
                </div>
                <div className="mt-6">
                  <Label className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">{c.labels.message}</Label>
                  <Textarea className={`${inputCls} h-auto min-h-[140px] py-3 resize-none`} value={form.message} onChange={set("message")} placeholder={c.ph.message} data-testid="input-message" />
                </div>
                <TurnstileWidget ref={turnstileRef} onToken={handleToken} onError={handleTsError} />
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold mt-8 w-full border border-[var(--al-gold)] py-4 font-body text-[12px] tracking-[0.22em] uppercase text-[var(--al-gold)] disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  <span className="flex items-center justify-center gap-2">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? c.submitting : c.submit}
                  </span>
                </button>
              </motion.form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
